import { type NavlibControllerUpdate } from "@3dconnexion/3dconnexionjs";
import { t } from "i18next";

export const SPACE_MOUSE_COMMANDS = {
    Properties: "ID_PROPERTIES",
    ObjectTree: "ID_OBJECT_TREE",
    Measure: "ID_MEASURE",
    Confirm: "ID_CONFIRM",
    Cancel: "ID_CANCEL",
    Section: "ID_SECTION",
    ClearSection: "ID_CLEAR_SECTION",
    ClearMeasurements: "ID_CLEAR_MEASUREMENTS",
    Undo: "ID_UNDO",
    Redo: "ID_REDO",
    Hide: "ID_HIDE",
    UnhideLast: "ID_UNHIDE_LAST",
    Isolate: "ID_ISOLATE",
    FlyTo: "ID_FLY_TO",
    ClearSelection: "ID_CLEAR_SELECTION",
    ClearView: "ID_CLEAR_VIEW",
    Home: "ID_HOME",
    Ortho: "ID_ORTHO",
    XRay: "ID_XRAY",
} as const;

export type SpaceMouseCommandId = (typeof SPACE_MOUSE_COMMANDS)[keyof typeof SPACE_MOUSE_COMMANDS];
export type SpaceMouseCommandHandlers = Partial<Record<SpaceMouseCommandId, () => void>>;

const SPACE_MOUSE_COMMAND_IDS: ReadonlySet<string> = new Set(Object.values(SPACE_MOUSE_COMMANDS));

export function isSpaceMouseCommand(commandId: string): commandId is SpaceMouseCommandId {
    return SPACE_MOUSE_COMMAND_IDS.has(commandId);
}

const ACTION_SET_ID = "Default";

type Action = {
    id: SpaceMouseCommandId;
    label: string;
    description: string;
    type: 2;
};

type Category = {
    id: string;
    label: string;
    type: 1;
    nodes: Action[];
};

const action = (id: SpaceMouseCommandId): Action => ({
    id,
    label: t(`spaceMouse.commands.labels.${id}`),
    description: t(`spaceMouse.commands.descriptions.${id}`),
    type: 2,
});

const category = (id: string, labelKey: string, nodes: Action[]): Category => ({
    id,
    label: t(`spaceMouse.commands.categories.${labelKey}`),
    nodes,
    type: 1,
});

/**
 * Build the command tree in the active UI language. Labels and descriptions are what the driver
 * shows in its button editor and radial menus, so they follow the viewer's locale; the ids do not.
 *
 * Stable action ids are persisted in the user's 3DxWare profile. Do not rename them after release.
 * Navigation mode is not in this tree. The 3Dconnexion driver owns Object, Fly, and Drone, including
 * mapping them to buttons and radial menus (BM-15039).
 * The POJO matches the SDK ActionTree wire shape without depending on constructors absent in its CJS build.
 */
export function buildSpaceMouseApplicationCommands(): NavlibControllerUpdate {
    return {
        commands: {
            activeSet: ACTION_SET_ID,
            tree: {
                nodes: [
                    {
                        id: ACTION_SET_ID,
                        label: ACTION_SET_ID,
                        type: 0,
                        nodes: [
                            category("CAT_PANELS", "panels", [
                                action(SPACE_MOUSE_COMMANDS.Properties),
                                action(SPACE_MOUSE_COMMANDS.ObjectTree),
                            ]),
                            category("CAT_TOOLS", "tools", [
                                action(SPACE_MOUSE_COMMANDS.Measure),
                                action(SPACE_MOUSE_COMMANDS.Section),
                                action(SPACE_MOUSE_COMMANDS.Confirm),
                                action(SPACE_MOUSE_COMMANDS.Cancel),
                                action(SPACE_MOUSE_COMMANDS.ClearMeasurements),
                                action(SPACE_MOUSE_COMMANDS.ClearSection),
                            ]),
                            category("CAT_EDIT", "edit", [
                                action(SPACE_MOUSE_COMMANDS.Undo),
                                action(SPACE_MOUSE_COMMANDS.Redo),
                            ]),
                            category("CAT_SELECTION", "selection", [
                                action(SPACE_MOUSE_COMMANDS.Hide),
                                action(SPACE_MOUSE_COMMANDS.UnhideLast),
                                action(SPACE_MOUSE_COMMANDS.Isolate),
                                action(SPACE_MOUSE_COMMANDS.FlyTo),
                                action(SPACE_MOUSE_COMMANDS.ClearSelection),
                                action(SPACE_MOUSE_COMMANDS.XRay),
                            ]),
                            category("CAT_CAMERA", "camera", [
                                action(SPACE_MOUSE_COMMANDS.Home),
                                action(SPACE_MOUSE_COMMANDS.ClearView),
                                action(SPACE_MOUSE_COMMANDS.Ortho),
                            ]),
                        ],
                    },
                ],
            },
        },
    };
}

export function runSpaceMouseCommand(commandId: string, handlers: SpaceMouseCommandHandlers): void {
    handlers[commandId as SpaceMouseCommandId]?.();
}
